import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { paidDayOffFields } from '../../../config/viewDocumentsFields';
import InfoIcon from "../../../../public/assets/images/info-circle-small-solid.svg";
import CustomDatepicker from '../../../components/common/Forms/Datepicker';
import ScrollToTopBtn from '../../../components/common/ScrollToTopBtn';
import { validateForm } from '../../../utils/validationEngine';
import { savePaidOffTime, fetchTimeOffDetails } from '../../../store/slices/Citizenship/managerPortalSlice';
import Notifications from '../../../components/common/Notifications';
import Loader from '../../../components/common/Loader';
import { trackButtonClick, trackFormSave, trackFormError, trackCustomPageView } from '../../../services/analytics';

interface AddPaidDayOffProps {
  empNuid?: string | null;
}

const AddPaidDayOff: React.FC<AddPaidDayOffProps> = ({ empNuid }) => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const nuidFromState = empNuid ?? ((location.state as any)?.nuid ?? null);
  const dispatch = useAppDispatch();
  const { savingPaidTimeOff, savePaidTimeOffError, timeOffDetails, loadingTimeOffDetails } = useAppSelector((state) => state.managerPortal);
  const [validationError, setValidationError] = useState<string | null>(null);

  const initialState = paidDayOffFields.reduce<Record<string, string>>((acc, f) => {
    acc[f.name] = '';
    return acc;
  }, {});

  const [formValues, setFormValues] = useState<Record<string, string>>(initialState);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [bannerHeader, setBannerHeader] = useState<string>('');
  const [notificationMessage, setNotificationMessage] = useState<string>('');
  const [showNotification, setShowNotification] = useState<boolean>(false);

   const formatDate = (date: string) => {
    if (!date) return '';
    return date.replace(/-/g, '/');
  };
  useEffect(() => {
    if (id) {
      dispatch(fetchTimeOffDetails({ applicationId: id, empNuid: nuidFromState ?? undefined }));
    }
  }, [id, dispatch, nuidFromState]);

  useEffect(() => {
    if (timeOffDetails) {
      setFormValues((prev) => ({
        ...prev,
        paidDayOffhours: timeOffDetails.paidTimeOffHours ? formatDate(String(timeOffDetails.paidTimeOffHours)) : '',
        paidDayOffdate: timeOffDetails.paidTimeOffDate ? formatDate(String(timeOffDetails.paidTimeOffDate)) : '',
      }));
    }
  }, [timeOffDetails]);

  // Show server-side save error in banner when present
  useEffect(() => {
    if (savePaidTimeOffError) {
      setBannerHeader(savePaidTimeOffError);
      setShowNotification(false);
    }
  }, [savePaidTimeOffError]);

   
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValidationError(null);
    setFormValues((prev) => ({ ...prev, [name]: value }));
    if (!value && !formValues.paidDayOffdate){
         setFieldErrors({});
         setBannerHeader('');
    }
  };

  const handleDateChange = (name: string, value: string) => {
    setValidationError(null);
    setFormValues((prev) => ({ ...prev, [name]: value }));
    if (!value && !formValues.paidDayOffhours){
         setFieldErrors({});
         setBannerHeader('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.target as HTMLInputElement).type === 'number') {
      if (e.key === '-' || e.key === 'e' || e.key === '.' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
      }
    }
  };

  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    (e.target as HTMLInputElement).blur();
  };

  const handleClear = () => {
    setFormValues(initialState);
    setFieldErrors({});
    setBannerHeader('');
  };

  const handleSave = async () => {
    setValidationError(null);
    console.log(`📊 Analytics: Save paid day off clicked - Application ${id}`);
    trackButtonClick('Save', 'manager-paid-day-off', { applicationId: id });

    if (!id) {
      setValidationError('Application ID is missing');
      trackFormError('citizenship', ['applicationId']);
      return;
    }

    const validationResult = validateForm(paidDayOffFields, formValues);
    if (validationResult.requiredIfErrors && Object.keys(validationResult.requiredIfErrors).length > 0) {
      const errorsToShow = validationResult.requiredIfErrors;
      let header = 'Form has errors';
      if (errorsToShow['paidDayOffhours']) {
        header = 'Paid day off hours is required when a date is entered.';
      } else if (errorsToShow['paidDayOffdate']) {
        header = 'Paid day off date is required when hours are entered.';
      }
      setFieldErrors(errorsToShow);
      setBannerHeader(header);
      trackFormError('citizenship', Object.keys(errorsToShow));
      return;
    }

    if (!validationResult.isValid) {
      let header = '';
      let errorsToShow: Record<string, string> = {};
      
      if (validationResult.errors['paidDayOffhours']) {
        errorsToShow['paidDayOffhours'] = validationResult.errors['paidDayOffhours'];
        header = 'Paid day off hours is not valid.';
      } else if (validationResult.errors['paidDayOffdate']) {
        errorsToShow['paidDayOffdate'] = validationResult.errors['paidDayOffdate'];
        header = 'Paid off date is not valid';
      }
      
      setFieldErrors(errorsToShow);
      setBannerHeader(header);
      trackFormError('citizenship', Object.keys(errorsToShow));
      return;
    }

    const paidTimeOffHours = formValues.paidDayOffhours;
    let paidTimeOffDate = formValues.paidDayOffdate;

    if (!paidTimeOffHours || !paidTimeOffDate) {
      setValidationError('Time and date are required');
      return;
    }

    const dateObj = new Date(paidTimeOffDate);
    paidTimeOffDate = `${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}-${dateObj.getFullYear()}`;
    setFieldErrors({});
    setBannerHeader('');
    const result = await dispatch(
      savePaidOffTime({
        applicationId: id,
        paidTimeOffHours,
        paidTimeOffDate,
        empNuid: nuidFromState ?? undefined,
      })
    );

    if (savePaidOffTime.fulfilled.match(result)) {
      const successMessage = result.payload?.message || 'Paid day off saved successfully';
      console.log(`📊 Analytics: Paid day off saved successfully - Application ${id}`);
      trackFormSave('citizenship', undefined, { applicationId: id, paidTimeOffHours, paidTimeOffDate });
      trackCustomPageView('manager-portal:paid-hours-saved');
      setNotificationMessage(successMessage);
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 20000);
    } else if (savePaidOffTime.rejected.match(result)) {
      trackFormError('citizenship', ['save']);
      setBannerHeader((result.payload as string) || 'Failed to save paid day off');
    }
  };

  return (
    <div className='min-h[67vh] mobile:px-5!'>
      {(savingPaidTimeOff || loadingTimeOffDetails) && <Loader />}
      {showNotification && (
        <Notifications messageInfo={notificationMessage} status={true} />
      )}
      {bannerHeader && (
        <div
          className="ds-banner ds-banner__bg--white ds-banner--icon p-6! mb-6! border-red-600!"
          data-ds-variant="basic"
          data-ds-version="1"
          data-ds-theme="vessel"
          role="alert"
          aria-live="assertive"
        >
          <div className="ds-banner__icon-img">
            <img
              src="/assets/images/errorCircleSmall.svg"
              alt=""
              style={{ width: '42px', height: '42px' }}
            />
          </div>
          <div className="ds-banner__container">
            <div className="ds-banner__main flex flex-col">
              <div
                className="ds-banner__main-header--awareness"
                style={{ fontWeight: 350, fontSize: '20px', marginBottom: '6px' }}
              >
                {bannerHeader}
              </div>
              <div className="ds-banner__main-body" style={{ fontSize: '16px' }}>
                <div className="mt-2">
                  {Object.entries(fieldErrors).map(([fieldName, errorMessage]) => {
                    const fieldLabel = paidDayOffFields.find(f => f.name === fieldName)?.label || fieldName;
                    return (
                      <div key={fieldName} className="mb-0!">
                        {fieldLabel}: {errorMessage}{' '}
                        <button
                          type="button"
                          onClick={() => {
                            const fieldElement = document.getElementById(fieldName) || 
                              document.getElementById(`date-input-${fieldName}`);
                            if (fieldElement) {
                              fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              fieldElement.focus();
                            }
                          }}
                          className="text-[#0074AD]! underline"
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                        >
                          View field.
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <h3 className="h3">Time off details</h3>
      <p className='p2 -book mobile:mt-8! desktop:mt-6!'>Select an option for each field . All fields are optional</p>


      <div className="flex flex-col mt-6">
        <div className="mx-auto w-full max-w-[420px]">
          <form className="w-full">
            <div className="grid grid-cols-1 gap-y-4 w-full">
            {paidDayOffFields.map((field) => (
              <div key={field.name} className="w-full min-w-0">
                {field.type === 'date' ? (
                  <div className="ds-form w-full relative" data-ds-theme="vessel" data-ds-variant="input" data-ds-version="1">
                    <CustomDatepicker
                      name={field.name}
                      label={field.label}
                      value={formValues[field.name] ?? ''}
                      required={field.required}
                      helperText={field.helperText}
                      error={fieldErrors[field.name]}
                      onChange={handleDateChange}
                      disabled={field.disabled}
                    />
                    {field.name === 'paidDayOffdate' && validationError?.includes('MM-DD-YYYY') && (
                      <span className="text-red-600 text-sm mt-1 block">{validationError}</span>
                    )}
                  </div>
                ) : (
                  <div className="ds-form w-full" data-ds-theme="vessel" data-ds-variant="input" data-ds-version="1">
                    <label htmlFor={field.name}>{field.label}</label>
                    <div className="w-full relative">
                      <input
                        id={field.name}
                        name={field.name}
                        type={field.type}
                        inputMode={field.type === 'number' ? 'numeric' : undefined}
                        value={formValues[field.name] ?? ''}
                        onChange={handleInputChange}
                        onInput={(e) => {
                          if (field.type === 'number') {
                            const input = e.currentTarget;
                            input.value = input.value.replace(/[^0-9.]/g, '').replace(/(\..*?)\..*/g, '$1').replace(/^0[^.]/, '0');
                          }
                        }}
                        onKeyDown={handleKeyPress}
                        onWheel={handleWheel}
                        className={`w-full min-w-0 block ${field.disabled ? 'ds-form__disabled-field bg-gray-100 text-gray-600' : ''} ${fieldErrors[field.name] ? 'field-error' : ''}`}
                        disabled={field.disabled}
                        required={field.required}
                        placeholder={field.placeholder}
                        style={{width:"100%"}}
                        aria-describedby={`${field.name}-error-description-list-1`}
                        aria-invalid={fieldErrors[field.name] ? 'true' : 'false'}
                      />
                      {fieldErrors[field.name] ? (
                        <div className="error-description">
                          <ul id={`${field.name}-error-description-list-1`}>
                            <li>
                             <img src="/assets/images/errorCircleSmall.svg" alt="Error icon"/>
                              <span className="screenreader-only">Error</span>
                              {fieldErrors[field.name]}
                            </li>
                          </ul>
                        </div>
                      ) : field.helperText && <span id={`${field.name}-helpertext`} className="ds-form__text-input-helper">{field.helperText}</span>}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </form>
      </div>
    </div>

      <div className="flex flex-col-reverse desktop:flex-row gap-3 desktop:mt-8!">
        <button
          onClick={handleClear}
          className="button -light  max-w-none!"
          data-analytics-type="button"
          data-analytics-click="Default"
        >
          Clear
        </button>
        <button
          onClick={handleSave}
          disabled={savingPaidTimeOff}
          className={`button -primary -light max-w-none! ${savingPaidTimeOff ? 'opacity-50 cursor-not-allowed' : ''}`}
          data-analytics-type="button"
          data-analytics-click="Default"
        >
          {savingPaidTimeOff ? 'Saving...' : 'Save'}
        </button>
      </div>

      <div className="flex gap-2 mt-4!">
        <img src={InfoIcon} alt="info icon" />
        <p className="p3 -book">
          If you select clear button you may need to re-enter your description
          and re-upload documentation
        </p>
      </div>
      <ScrollToTopBtn/>
    </div>
  );
};

export default AddPaidDayOff;


